import { PricingTier, Api } from './types';

// Configuration
// Handle both Vite (import.meta.env) and standard process.env
const getEnv = (key: string) => {
  // @ts-ignore
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    // @ts-ignore
    return import.meta.env[key] || import.meta.env[`VITE_${key}`];
  }
  // @ts-ignore
  return typeof process !== 'undefined' ? process.env[key] : '';
};

export const POSTHOG_API_KEY = getEnv('POSTHOG_KEY') || '';
export const POSTHOG_HOST = 'https://app.posthog.com';

// Content
export const SAAS_TIERS: PricingTier[] = [
  {
    name: "Free",
    price: "$0",
    monthlyPrice: 0,
    period: "/mo",
    description: "Get started with no commitment.",
    features: [
      '25 Lookups / mo',
      '5 CSV Exports / mo',
      'Basic Search',
      'Referral Bonuses'
    ],
    cta: "Sign Up Free",
    highlight: false
  },
  {
    name: "Starter",
    price: "$99",
    monthlyPrice: 99,
    period: "/mo",
    description: "For solo consultants and small teams.",
    features: [
      '5,000 Credits / mo',
      'FL Data Access',
      'Basic Search',
      'Email Support'
    ],
    cta: "Subscribe",
    highlight: false
  },
  {
    name: "Growth",
    price: "$249",
    monthlyPrice: 249,
    period: "/mo",
    description: "For growing agencies scaling outreach.",
    features: [
      "15,000 Credits / mo",
      "Unlimited Searches",
      "New License Alerts",
      "API Key Included",
      "Priority Support"
    ],
    cta: "Subscribe",
    highlight: true,
    recommended: true
  },
  {
    name: "Agency",
    price: "$499",
    monthlyPrice: 499,
    period: "/mo",
    description: "High volume for lead gen firms.",
    features: [
      '30,000 Credits / mo',
      '15 Major Markets Access',
      'Dedicated Account Mgr',
      'White Label Reports',
      'API Access + Webhooks'
    ],
    cta: "Start Trial",
    highlight: false
  }
];

export const TEAM_TIERS: PricingTier[] = [
  {
    name: "Team",
    price: "$199",
    monthlyPrice: 199,
    period: "per user/mo",
    description: "Collaborative workspace for your organization.",
    features: [
      "Minimum 3 seats",
      "Shared Credit Pool",
      "Admin Usage Dashboard",
      "Centralized Billing",
      "Team API Keys"
    ],
    cta: "Create Team",
    highlight: false
  },
  {
    name: "Enterprise",
    price: "Custom",
    monthlyPrice: 0,
    period: "",
    description: "Security, compliance, and unlimited scale.",
    features: [
      "Unlimited Volume",
      "SSO / SAML Enforcement",
      "Dedicated CSM & Slack",
      "Custom Data Enrichment",
      "MSA & Invoicing"
    ],
    cta: "Contact Sales",
    highlight: false
  }
];

export const API_PRICING: Api[] = [
  {
    title: "Search",
    price: "$0.03",
    unit: "per request",
    description: "Standard DB lookup. Fresh (Daily/Weekly)."
  },
  {
    title: "Enrich",
    price: "$0.10",
    unit: "per request",
    description: "Add Phone & Email (Beta)."
  },
  {
    title: "Live Verify",
    price: "$0.25",
    unit: "per request",
    description: "Real-time pulse check against state board."
  },
  {
    title: "Bulk",
    price: "$2.00",
    unit: "per 100 records",
    description: "Mass CSV Export via async job."
  }
];

// Verified sellable states (1000+ records as of 2026-01-20)
export const SELLABLE_STATES = [
  { code: 'FL', name: 'Florida', records: 5376167 },
  { code: 'NY', name: 'New York', records: 3770910 },
  { code: 'CT', name: 'Connecticut', records: 1364017 },
  { code: 'CA', name: 'California', records: 936368 },
  { code: 'UT', name: 'Utah', records: 507146 },
  { code: 'WA', name: 'Washington', records: 311318 },
  { code: 'VA', name: 'Virginia', records: 219602 },
  { code: 'NV', name: 'Nevada', records: 196465 },
  { code: 'IL', name: 'Illinois', records: 175722 },
  { code: 'DE', name: 'Delaware', records: 146310 },
  { code: 'TX', name: 'Texas', records: 113841 },
  { code: 'AK', name: 'Alaska', records: 30020 },
  { code: 'CO', name: 'Colorado', records: 21836 },
  { code: 'WV', name: 'West Virginia', records: 17775 },
  { code: 'NJ', name: 'New Jersey', records: 5866 },
];

export const DATA_PACKS = [
  {
    id: 'single_state',
    name: 'Single State',
    price: 297,
    description: 'Complete license database for one state',
    features: [
      'All Professions',
      'Full Contact Info',
      'One-time Purchase',
      'CSV Download',
      'Lifetime Updates'
    ],
    states_count: 1,
    available_states: SELLABLE_STATES.map(s => s.code),
    stripePriceId: 'price_1Scs1ZB7WWia5jDec083ojgW'
  },
  {
    id: 'regional',
    name: 'Regional',
    price: 597,
    description: 'Any 3 states from our coverage',
    features: [
      '3 State Licenses',
      'Regional Coverage',
      'One-time Purchase',
      'CSV Download',
      'Priority Support'
    ],
    states_count: 3,
    available_states: SELLABLE_STATES.map(s => s.code),
    stripePriceId: 'price_1Scs1aB7WWia5jDerEreqg5Z'
  },
  {
    id: 'national',
    name: 'National',
    price: 997,
    description: 'Top 10 major markets',
    features: [
      '10 Major Markets',
      'High Volume Data',
      'One-time Purchase',
      'CSV Download',
      'Dedicated Account Mgr'
    ],
    states_count: 10,
    available_states: SELLABLE_STATES.slice(0, 10).map(s => s.code),
    stripePriceId: 'price_1Scs1aB7WWia5jDetBINzvow',
    highlight: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 1997,
    description: 'All 15 available states',
    features: [
      '15 Major Markets',
      'License Records from State Boards',
      'API Access Included',
      'Daily Updates',
      'White Label Rights'
    ],
    states_count: 15,
    available_states: SELLABLE_STATES.map(s => s.code),
    stripePriceId: 'price_1Scs1bB7WWia5jDeigGDXACU'
  }
];